import { httpClient } from "@bw/shared/api";
import type { Schema } from "@bw/shared/types/generated";

export type CourtAvailabilityPreviewBody =
  Schema["CourtAvailabilityPreviewRequest"];
export type CourtAvailabilityConflict =
  Schema["CourtAvailabilityConflictSummary"];
export type CourtAvailabilityCourtVerdict = Omit<
  Schema["CourtAvailabilityCourtVerdict"],
  "conflicts" | "tags"
> & {
  conflicts: CourtAvailabilityConflict[];
  tags: string[];
};
export type CourtAvailabilityPreview = Omit<
  Schema["CourtAvailabilityPreviewResponse"],
  "courts"
> & {
  courts: CourtAvailabilityCourtVerdict[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isInstant(value: unknown): value is string {
  return (
    typeof value === "string" &&
    Number.isFinite(Date.parse(value)) &&
    /(?:Z|[+-]\d{2}:\d{2})$/.test(value)
  );
}

function isTimeZone(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

function sameInstant(left: string, right: string): boolean {
  return Date.parse(left) === Date.parse(right);
}

function isConflict(value: unknown): value is CourtAvailabilityConflict {
  return (
    isRecord(value) &&
    typeof value.event_type === "string" &&
    (value.source_type == null || typeof value.source_type === "string") &&
    isInstant(value.start_time) &&
    isInstant(value.end_time) &&
    Date.parse(value.end_time) > Date.parse(value.start_time)
  );
}

const UNAVAILABLE_REASONS = new Set([
  "court_disabled",
  "outside_operating_hours",
  "court_conflict",
]);

function isCourtVerdict(
  value: unknown,
  expectedCourtIds: ReadonlySet<string>,
  body: CourtAvailabilityPreviewBody,
): value is CourtAvailabilityCourtVerdict {
  if (
    !isRecord(value) ||
    typeof value.court_id !== "string" ||
    !expectedCourtIds.has(value.court_id) ||
    typeof value.court_name !== "string" ||
    !Array.isArray(value.tags) ||
    !value.tags.every((tag) => typeof tag === "string") ||
    !(
      value.max_players == null ||
      (Number.isInteger(value.max_players) && Number(value.max_players) > 0)
    ) ||
    typeof value.available !== "boolean" ||
    !Array.isArray(value.conflicts) ||
    !value.conflicts.every(isConflict) ||
    !(value.available_until == null || isInstant(value.available_until))
  ) {
    return false;
  }

  if (value.available) {
    if (value.unavailable_reason != null || !isInstant(value.available_until))
      return false;
    const requestedStart = Date.parse(body.start_time);
    const safeEnd = Date.parse(value.available_until);
    const conflictsClaimedSafeInterval = value.conflicts.some((conflict) => {
      const conflictStart = Date.parse(conflict.start_time);
      const conflictEnd = Date.parse(conflict.end_time);
      return conflictStart < safeEnd && requestedStart < conflictEnd;
    });
    if (conflictsClaimedSafeInterval) return false;
    const safeMinutes = (safeEnd - requestedStart) / 60_000;
    return (
      safeMinutes >= body.minimum_duration_minutes &&
      Date.parse(value.available_until) <= Date.parse(body.end_time)
    );
  }

  return (
    typeof value.unavailable_reason === "string" &&
    UNAVAILABLE_REASONS.has(value.unavailable_reason)
  );
}

function decodePreview(
  value: unknown,
  facilityId: string,
  body: CourtAvailabilityPreviewBody,
): CourtAvailabilityPreview | null {
  if (
    !isRecord(value) ||
    value.facility_id !== facilityId ||
    value.is_binding !== false ||
    value.minimum_duration_minutes !== body.minimum_duration_minutes ||
    !isInstant(value.start_time) ||
    !isInstant(value.end_time) ||
    !sameInstant(value.start_time, body.start_time) ||
    !sameInstant(value.end_time, body.end_time) ||
    !isInstant(value.checked_at) ||
    !isTimeZone(value.timezone) ||
    !Array.isArray(value.courts)
  ) {
    return null;
  }

  const expectedCourtIds = new Set<string>(body.court_ids);
  if (
    expectedCourtIds.size !== body.court_ids.length ||
    value.courts.length !== expectedCourtIds.size ||
    !value.courts.every((court) =>
      isCourtVerdict(court, expectedCourtIds, body),
    )
  ) {
    return null;
  }
  const returnedCourtIds = new Set(value.courts.map((court) => court.court_id));
  if ([...expectedCourtIds].some((courtId) => !returnedCourtIds.has(courtId)))
    return null;
  return value as CourtAvailabilityPreview;
}

export async function previewCourtAvailability(
  facilityId: string,
  body: CourtAvailabilityPreviewBody,
): Promise<CourtAvailabilityPreview> {
  const value = await httpClient.post<unknown>(
    `/facilities/${facilityId}/court-availability/preview`,
    body,
  );
  const decoded = decodePreview(value, facilityId, body);
  if (!decoded) throw new Error("Court availability response was invalid.");
  return decoded;
}
